import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, subDays, startOfDay } from "date-fns";
import { Building2, Search, Loader2, RefreshCw, ExternalLink, Linkedin, X, ChevronDown, RotateCcw } from "lucide-react";
import { LineChart, Line, BarChart, Bar } from "recharts";
import {
  getAllCachedStories, getCompanyNews, setCompanyNewsCache,
  getSearchedCompanies, getSearchedCompany, getRelativeTime, setNewsStoriesCache,
  updateSearchedCompanyStatus, clearCompanyNewsCacheEntry,
  addSearchedCompany, getConfirmedWebsite, setConfirmedWebsite,
  removeSearchedCompany, storeReportId, getStoredReportId,
} from "@/lib/cache";
import { getSmartSources } from "@/lib/sourceUtils";
import { API } from "@/App";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PORTFOLIO_SEED, SECTOR_CATEGORIES } from "@/data/portfolioIntelligence";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const deriveNamePermalink = (name) =>
  (name || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

function parseWebsiteInput(raw) {
  let v = (raw || "").trim();
  if (!v) return null;
  return v.replace(/^https?:\/\//, "").replace(/\/.*$/, "").trim() || null;
}
function buildStartPayload(cw) {
  return cw.includes(".") ? { website: cw } : { permalink: cw };
}
function formatDisplayName(entry) {
  const n = entry.name || "";
  if (n && n !== entry.permalink && n !== entry.domain && !n.match(/^[a-z0-9-]+$/)) return n;
  const slug = entry.name || entry.permalink || entry.domain || "";
  return slug.split("-").map(w => w ? w.charAt(0).toUpperCase() + w.slice(1) : "").join(" ");
}

// Task 3: Check both seed permalink and name-derived permalink for Company News data
function hasCompanyNewsDataEx(permalink, companyName) {
  // Only returns a match when articles are actually cached — never just a stored report_id.
  // If a report_id exists without articles, we want the CompanyActionModal to open pre-filled.
  const check = (pl) => {
    if (!pl) return null;
    const cached = getCompanyNews(pl);
    return (cached?.articles?.length > 0) ? pl : null;
  };
  // 1. Seed permalink (e.g., "wiz-inc")
  const r1 = check(permalink);
  if (r1) return r1;
  // 2. Name-derived permalink (e.g., "wiz")
  const np = deriveNamePermalink(companyName || "");
  if (np && np !== permalink) {
    const r2 = check(np);
    if (r2) return r2;
  }
  // 3. Website-derived permalink — handles key mismatch when CompanyNews page stored
  //    data via website (e.g., "wiz.io" → stored as "wizio", but seed key is "wiz-inc")
  const seedWebsite = PORTFOLIO_SEED[permalink]?.website || "";
  if (seedWebsite) {
    const wp = deriveNamePermalink(seedWebsite); // "wiz.io" → "wizio", "bytedance.com" → "bytedancecom"
    if (wp && wp !== permalink && wp !== np) {
      const r3 = check(wp);
      if (r3) return r3;
    }
    // Also try just the domain stem without TLD ("wiz.io" → "wiz", "openai.com" → "openai")
    const domainStem = seedWebsite.replace(/\.[^.]+$/, ""); // strip last .xxx
    const sp = deriveNamePermalink(domainStem);
    if (sp && sp !== permalink && sp !== np && sp !== wp) {
      const r4 = check(sp);
      if (r4) return r4;
    }
  }
  return null;
}

function getTopStories(stories, companyName, n = null) {
  const filtered = stories
    .filter(s => (s.portfolio_impact || []).some(p => p.company_name === companyName))
    .sort((a, b) => new Date(b.updated_at || b.first_seen_at || 0) - new Date(a.updated_at || a.first_seen_at || 0));
  return n ? filtered.slice(0, n) : filtered;
}

// ─── Task 1: Dynamic trend color ─────────────────────────────────────────────
function getTrendColor(arr) {
  const vals = (arr || []).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return "#9CA3AF";
  const first = vals[0];
  const last = vals[vals.length - 1];
  if (!first || first === 0) return "#9CA3AF";
  const pct = (last - first) / Math.abs(first);
  if (pct > 0.05) return "#16A34A";
  if (pct < -0.05) return "#DC2626";
  return "#9CA3AF";
}

// ─── Formatters ───────────────────────────────────────────────────────────────
function fmtVal(b) {
  if (b == null) return "—";
  if (b >= 1) return `$${b >= 10 ? Math.round(b) : b.toFixed(1)}B`;
  return `$${Math.round(b * 1000)}M`;
}
function fmtRev(m) {
  if (m == null) return "—";
  if (m >= 1000) return `$${(m / 1000).toFixed(1)}B`;
  return `$${m}M`;
}
function fmtTraffic(m) {
  if (m == null) return "—";
  if (m >= 1000) return `${(m / 1000).toFixed(1)}B/mo`;
  if (m >= 1) return `${Math.round(m)}M/mo`;
  return `${Math.round(m * 1000)}K/mo`;
}
function calcDelta(arr) {
  const vals = (arr || []).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return null;
  const prev = vals[vals.length - 2];
  const latest = vals[vals.length - 1];
  if (!prev || prev === 0) return null;
  return Math.round((latest - prev) / Math.abs(prev) * 100);
}

// ─── Sparklines ───────────────────────────────────────────────────────────────
function LineSparkline({ data, color }) {
  const vals = (data || []).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return <span style={{ color: "#9CA3AF", fontSize: "11px" }}>—</span>;
  return (
    <LineChart width={80} height={26} data={vals.map(v => ({ v }))} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
      <Line type="monotone" dataKey="v" stroke={color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
    </LineChart>
  );
}
function BarSparkline({ data, color }) {
  const vals = (data || []).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return <span style={{ color: "#9CA3AF", fontSize: "11px" }}>—</span>;
  return (
    <BarChart width={80} height={26} data={vals.map(v => ({ v }))} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
      <Bar dataKey="v" fill={color} isAnimationActive={false} radius={[2, 2, 0, 0]} />
    </BarChart>
  );
}
function DeltaPill({ value }) {
  if (value == null) return null;
  const pos = value >= 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: "10px", fontWeight: 600, padding: "1px 6px", borderRadius: "999px",
      background: pos ? "#DCFCE7" : "#FEE2E2",
      color: pos ? "#15803D" : "#DC2626",
    }}>
      {pos ? "+" : ""}{value}%
    </span>
  );
}

// ─── Seed Data Cells ──────────────────────────────────────────────────────────
const DASH = <span style={{ color: "#D1D5DB", fontSize: "12px" }}>—</span>;

function ValuationCell({ seedRow }) {
  if (!seedRow?.valuation_b) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const delta = calcDelta(seedRow.val_trend);
  const latest = seedRow.val_trend?.slice(-1)[0] ?? seedRow.valuation_b;
  const prev = seedRow.val_trend?.slice(-2, -1)[0];
  const up = prev != null ? latest >= prev : true;
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ display: "inline-block", background: "#97C8A6", color: "#181B20", fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", marginBottom: "4px" }}>
        {fmtVal(seedRow.valuation_b)}
      </div>
      <LineSparkline data={seedRow.val_trend} color={up ? "#16A34A" : "#DC2626"} />
      <div style={{ marginTop: "2px" }}><DeltaPill value={delta} /></div>
    </div>
  );
}

function RevenueTrendCell({ seedRow }) {
  if (!seedRow?.rev_trend?.length) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const latest = seedRow.rev_trend.slice(-1)[0];
  const delta = calcDelta(seedRow.rev_trend);
  // Task 1: dynamic color
  const barColor = getTrendColor(seedRow.rev_trend);
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#0A0A0A", marginBottom: "4px" }}>{fmtRev(latest)}</div>
      <BarSparkline data={seedRow.rev_trend} color={barColor} />
      <div style={{ marginTop: "2px" }}><DeltaPill value={delta} /></div>
    </div>
  );
}

function HeadcountCell({ seedRow }) {
  if (!seedRow?.hc_trend?.length) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const latest = seedRow.hc_trend.slice(-1)[0];
  const delta = calcDelta(seedRow.hc_trend);
  // Task 1: dynamic color (with flat detection)
  const hcColor = getTrendColor(seedRow.hc_trend);
  const sig = seedRow.talent_signal || "";
  const sigStyle = sig.includes("↑") ? { bg: "#DCFCE7", text: "#15803D" } : sig.includes("↓") ? { bg: "#FEE2E2", text: "#DC2626" } : { bg: "#F3F4F6", text: "#6B7280" };
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#0A0A0A", marginBottom: "4px" }}>{latest != null ? latest.toLocaleString() : "—"}</div>
      <LineSparkline data={seedRow.hc_trend} color={hcColor} />
      <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
        <DeltaPill value={delta} />
        {sig && <span style={{ fontSize: "10px", fontWeight: 600, padding: "1px 6px", borderRadius: "999px", background: sigStyle.bg, color: sigStyle.text }}>{sig}</span>}
      </div>
    </div>
  );
}

function TotalRaisedCell({ seedRow }) {
  if (!seedRow?.total_raised_m) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#0A0A0A" }}>{fmtRev(seedRow.total_raised_m)}</div>
      {seedRow.last_round && <div style={{ fontSize: "10px", color: "#9AA0A6", fontStyle: "italic", marginTop: "2px" }}>{seedRow.last_round}</div>}
    </div>
  );
}

function WebTrafficCell({ seedRow }) {
  if (!seedRow?.traffic_trend?.length) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const latest = seedRow.traffic_trend.slice(-1)[0];
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#0A0A0A", marginBottom: "4px" }}>{fmtTraffic(latest)}</div>
      <BarSparkline data={seedRow.traffic_trend} color="#2563EB" />
      <div style={{ fontSize: "9px", color: "#9AA0A6", marginTop: "2px" }}>Similarweb est.</div>
    </div>
  );
}

function LinkedInCell({ seedRow }) {
  if (!seedRow?.linkedin_followers) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <Linkedin size={13} style={{ color: "#0A66C2", flexShrink: 0 }} />
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#0A0A0A" }}>{seedRow.linkedin_followers}</span>
        <a href={`https://www.linkedin.com/company/${encodeURIComponent(seedRow.name || "")}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: "#9AA0A6", lineHeight: 1 }}><ExternalLink size={10} /></a>
      </div>
    </div>
  );
}

function GlassdoorCell({ seedRow }) {
  if (!seedRow?.glassdoor_rating) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const r = seedRow.glassdoor_rating;
  const bg = r >= 4.0 ? "#DCFCE7" : r >= 3.5 ? "#FEF3C7" : "#FEE2E2";
  const tc = r >= 4.0 ? "#15803D" : r >= 3.5 ? "#92400E" : "#DC2626";
  return (
    <div style={{ padding: "10px 12px" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "3px", background: bg, color: tc, padding: "3px 8px", borderRadius: "6px" }}>
        <span style={{ fontSize: "12px", fontWeight: 700 }}>{r.toFixed(1)}</span>
        <span style={{ fontSize: "11px" }}>★</span>
      </div>
      <div style={{ fontSize: "9px", color: "#9AA0A6", marginTop: "3px" }}>Glassdoor</div>
    </div>
  );
}

const SENTIMENT_PILL = {
  POSITIVE:   { bg: "#DCFCE7", text: "#15803D", label: "Positive" },
  NEGATIVE:   { bg: "#FEE2E2", text: "#DC2626", label: "Negative" },
  NEUTRAL:    { bg: "#F3F4F6", text: "#6B7280", label: "Neutral"  },
  CAUTIONARY: { bg: "#FEF3C7", text: "#92400E", label: "Cautionary" },
  MIXED:      { bg: "#DBEAFE", text: "#1D4ED8", label: "Mixed"   },
};
function SentimentCell({ sentiment }) {
  if (!sentiment) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const s = SENTIMENT_PILL[sentiment?.toUpperCase()] || SENTIMENT_PILL.NEUTRAL;
  return <div style={{ padding: "10px 12px" }}><span style={{ fontSize: "11px", fontWeight: 600, padding: "3px 8px", borderRadius: "999px", background: s.bg, color: s.text }}>{s.label}</span></div>;
}

function WebsiteCell({ seedRow }) {
  if (!seedRow?.website) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  return (
    <div style={{ padding: "10px 12px" }}>
      <a href={`https://${seedRow.website}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", color: "#2563EB", textDecoration: "none" }}
        onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
      >
        {seedRow.website}<ExternalLink size={10} />
      </a>
    </div>
  );
}

// ─── Task 2: Enrichment Cells with Inline Expand/Collapse ─────────────────────
function CoreOfferingCell({ seedRow }) {
  const [expanded, setExpanded] = useState(false);
  if (!seedRow?.core_offering) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const text = seedRow.core_offering;
  const isLong = text.length > 80;
  const display = expanded || !isLong ? text : text.slice(0, 80) + "…";
  return (
    <div style={{ padding: "10px 12px" }}>
      <span style={{ fontSize: "11px", color: "#3A3A3A", lineHeight: "1.45" }}>
        {display}
        {isLong && (
          <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
            style={{ fontSize: "10px", color: "#97C8A6", cursor: "pointer", background: "none", border: "none", marginLeft: "4px", padding: 0 }}>
            {expanded ? "less" : "more"}
          </button>
        )}
      </span>
    </div>
  );
}

function RevenueModelCell({ seedRow }) {
  if (!seedRow?.revenue_model) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const text = seedRow.revenue_model;
  const truncated = text.length > 48 ? text.slice(0, 48) + "…" : text;
  return (
    <div style={{ padding: "10px 12px" }}>
      <span title={text} style={{ fontSize: "10px", fontWeight: 500, padding: "2px 7px", borderRadius: "4px", background: "#F3F4F6", color: "#4B5563", display: "inline-block", cursor: "default", lineHeight: "1.5" }}>
        {truncated}
      </span>
    </div>
  );
}

function AICapabilitiesCell({ seedRow }) {
  const [expanded, setExpanded] = useState(false);
  if (!seedRow?.ai_capabilities || seedRow.ai_capabilities === "None") return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const text = seedRow.ai_capabilities;
  const isLong = text.length > 70;
  const display = expanded || !isLong ? text : text.slice(0, 70) + "…";
  return (
    <div style={{ padding: "10px 12px" }}>
      <span style={{ fontSize: "11px", color: "#3A3A3A", lineHeight: "1.4" }}>
        {display}
        {isLong && (
          <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
            style={{ fontSize: "10px", color: "#97C8A6", cursor: "pointer", background: "none", border: "none", marginLeft: "4px", padding: 0 }}>
            {expanded ? "less" : "more"}
          </button>
        )}
      </span>
    </div>
  );
}

function KeyFeaturesCell({ seedRow }) {
  const [expanded, setExpanded] = useState(false);
  if (!seedRow?.key_features?.length) return <div style={{ padding: "10px 12px" }}>{DASH}</div>;
  const all = seedRow.key_features;
  const showAll = expanded || all.length <= 3;
  const features = showAll ? all : all.slice(0, 3);
  const extra = all.length - 3;
  return (
    <div style={{ padding: "8px 12px" }}>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {features.map((f, i) => (
          <li key={i} style={{ fontSize: "10px", color: "#3A3A3A", lineHeight: "1.6", display: "flex", alignItems: "flex-start", gap: "4px" }}>
            <span style={{ color: "#97C8A6", flexShrink: 0, fontWeight: 700, marginTop: "1px" }}>•</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "165px" }} title={f}>{f}</span>
          </li>
        ))}
      </ul>
      {extra > 0 && (
        <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{ fontSize: "10px", color: "#97C8A6", background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: "2px" }}>
          {expanded ? "less" : `+${extra} more`}
        </button>
      )}
    </div>
  );
}

// ─── Category detection + News Articles Column ─────────────────────────────────
const CATEGORY_KEYWORDS = {
  "Fundraising":   { kws: ["funding", "raised", "round", "valuation", "investment", "series", "venture"], types: ["Funding", "Fundraising", "Investment", "Investments"] },
  "Partnerships":  { kws: ["partnership", "agreement", "collaboration", "deal", "signed"],                  types: ["Partnership", "Partnerships", "Alliance"] },
  "M&A":           { kws: ["acquisition", "merger", "acquired", "buyout", "takeover"],                     types: ["M&A", "Mergers", "Acquisition"] },
  "Product Launch":{ kws: ["launched", "released", "announced", "new product", "unveiled"],               types: ["Product launches", "Product Launch"] },
  "Regulatory":    { kws: ["compliance", "fine", "lawsuit", "investigation", "regulatory", "approved"],   types: ["Compliance", "Regulatory", "Legal"] },
  "Leadership":    { kws: ["ceo", "appointed", "hired", "resigned", "joins as", "executive"],              types: ["Leadership", "Management", "Hiring"] },
};
const CATEGORY_STYLES = {
  "Fundraising":   { bg: "#DCFCE7", text: "#16A34A" },
  "Partnerships":  { bg: "#DBEAFE", text: "#2563EB" },
  "M&A":           { bg: "#EDE9FE", text: "#7C3AED" },
  "Product Launch":{ bg: "#FEF3C7", text: "#D97706" },
  "Regulatory":    { bg: "#FEE2E2", text: "#DC2626" },
  "Leadership":    { bg: "#F3F4F6", text: "#6B7280" },
};

function detectCategories(items) {
  const counts = {};
  items.forEach(item => {
    const text = ((item.title || "") + " " + (item.ai_summary || "") + " " + (item.type || "")).toLowerCase();
    Object.entries(CATEGORY_KEYWORDS).forEach(([cat, { kws, types }]) => {
      const typeMatch = types.some(t => (item.type || "").toLowerCase().includes(t.toLowerCase()));
      const kwMatch = kws.some(kw => text.includes(kw));
      if (typeMatch || kwMatch) counts[cat] = (counts[cat] || 0) + 1;
    });
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([label]) => ({ label, ...CATEGORY_STYLES[label] }));
}

// Task 5: News Articles Cell — source display + fallback from stories + error state
function NewsArticlesCell({ permalink, companyName, topStories, onFetchNewsClick, onClearCache }) {
  const navigate = useNavigate();

  // Task 3: Check both seed permalink and name-derived permalink
  const effectivePermalink = hasCompanyNewsDataEx(permalink, companyName) || permalink;
  const cachedData = getCompanyNews(effectivePermalink);
  const reportId = getStoredReportId(effectivePermalink) || cachedData?.reportId;
  const storedCompany = getSearchedCompany(effectivePermalink) ||
    (effectivePermalink !== deriveNamePermalink(companyName || "") ? getSearchedCompany(deriveNamePermalink(companyName || "")) : null);
  const hasFetchError = storedCompany?.status === "error";

  if (!reportId && !cachedData?.articles?.length) {
    // Stories-based fallback (user message: "from stories" label)
    if (topStories && topStories.length > 0) {
      const cats = detectCategories(topStories.map(s => ({
        title: s.title || "",
        ai_summary: s.snippet || s.description || "",
        type: s.primary_tag || s.diligence_area || "",
      })));
      if (cats.length > 0) {
        return (
          <div style={{ padding: "8px 12px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {cats.slice(0, 3).map(cat => (
                <button key={cat.label}
                  onClick={e => { e.stopPropagation(); navigate(`/company-news?company=${encodeURIComponent(effectivePermalink)}&filter=${encodeURIComponent(cat.label)}`); }}
                  style={{ fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "999px", background: cat.bg, color: cat.text, cursor: "pointer", border: "none" }}
                  data-testid={`news-cat-stories-${permalink}-${cat.label}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: "10px", color: "#9CA3AF", marginTop: "3px" }}>from stories</div>
          </div>
        );
      }
    }
    if (hasFetchError) {
      return (
        <div style={{ padding: "10px 12px" }}>
          <button onClick={e => { e.stopPropagation(); onFetchNewsClick ? onFetchNewsClick() : navigate(`/company-news?company=${encodeURIComponent(effectivePermalink)}`); }}
            style={{ fontSize: "11px", color: "#DC2626", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            Fetch failed — retry
          </button>
        </div>
      );
    }
    return (
      <div style={{ padding: "10px 12px" }}>
        <button onClick={e => { e.stopPropagation(); onFetchNewsClick ? onFetchNewsClick() : navigate(`/company-news?company=${encodeURIComponent(effectivePermalink)}`); }}
          style={{ fontSize: "11px", color: "#9AA0A6", cursor: "pointer", background: "none", border: "none", padding: 0, textDecoration: "underline" }}
          data-testid={`fetch-news-link-${permalink}`}
        >
          Fetch news →
        </button>
      </div>
    );
  }

  const articles = cachedData?.articles || [];
  const categories = detectCategories(articles);
  const sourceDisplay = articles.length > 0 ? getSmartSources(articles) : null;

  return (
    <div style={{ padding: "8px 12px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
        {categories.length > 0 ? categories.slice(0, 3).map(cat => (
          <button key={cat.label}
            onClick={e => { e.stopPropagation(); navigate(`/company-news?company=${encodeURIComponent(effectivePermalink)}&filter=${encodeURIComponent(cat.label)}`); }}
            style={{ fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "999px", background: cat.bg, color: cat.text, cursor: "pointer", border: "none" }}
            data-testid={`news-cat-${permalink}-${cat.label}`}
          >
            {cat.label}
          </button>
        )) : (
          <button onClick={e => { e.stopPropagation(); navigate(`/company-news?company=${encodeURIComponent(effectivePermalink)}`); }}
            style={{ fontSize: "11px", color: "#2563EB", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            View {articles.length} articles →
          </button>
        )}
        {onClearCache && (
          <button
            onClick={e => { e.stopPropagation(); onClearCache(); }}
            title="Reset cached articles — lets you fetch a new report"
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#D1D5DB", padding: "2px", borderRadius: "4px", display: "flex", alignItems: "center", flexShrink: 0, transition: "color 150ms" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#D1D5DB"; }}
            data-testid={`reset-cache-btn-${permalink}`}
          >
            <RotateCcw size={11} />
          </button>
        )}
      </div>
      {sourceDisplay && (
        <div style={{ fontSize: "10px", color: "#6B7280", marginTop: "3px" }}>{sourceDisplay}</div>
      )}
    </div>
  );
}

// ─── Stories Modal ──────────────────────────────────────────────────────────────
function StoriesModal({ companyName, stories, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl" style={{ maxWidth: "600px", width: "90%", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()} data-testid="stories-modal">
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #DCDFE2", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 700, color: "#0A0A0A", margin: 0 }}>{companyName} — All Latest Stories</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9AA0A6" }}><X size={16} /></button>
        </div>
        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {stories.map((s, idx) => (
            <div key={s.brief_id} style={{ borderBottom: idx < stories.length - 1 ? "1px solid #F0F2F4" : "none", paddingBottom: "12px", marginBottom: "12px" }}>
              <button onClick={() => { onClose(); navigate(`/story/${s.brief_id}`, { state: { story: s } }); }}
                style={{ display: "block", textAlign: "left", fontFamily: "'Playfair Display', serif", fontSize: "13px", fontWeight: 500, color: "#0A0A0A", background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#97C8A6"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#0A0A0A"; }}
              >
                {s.title}
              </button>
              <div style={{ fontSize: "11px", color: "#9AA0A6", marginTop: "4px" }}>{getRelativeTime(s.updated_at || s.first_seen_at)}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid #DCDFE2", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "6px 16px", borderRadius: "6px", fontSize: "12px", fontWeight: 500, background: "#F4F5F6", border: "1px solid #DCDFE2", color: "#3A3A3A", cursor: "pointer" }} data-testid="stories-modal-close">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Flagged Cell ───────────────────────────────────────────────────────────────
function FlaggedCell({ companyId, flaggedCompanies }) {
  const isFlagged = flaggedCompanies?.some(fc => fc.company_id === companyId);
  return (
    <div style={{ padding: "12px 14px", textAlign: "center" }}>
      {isFlagged ? (
        <span style={{ 
          fontSize: "11px", 
          fontWeight: 600, 
          color: "#16A34A",
          background: "#DCFCE7",
          padding: "4px 10px",
          borderRadius: "4px",
        }}>
          Yes
        </span>
      ) : (
        <span style={{ 
          fontSize: "11px", 
          color: "#9AA0A6",
        }}>
          No
        </span>
      )}
    </div>
  );
}

// ─── Latest News Cell ──────────────────────────────────────────────────────────
function LatestNewsCell({ stories, companyName }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  if (!stories || stories.length === 0) {
    return <div style={{ padding: "12px 14px" }}><span style={{ fontSize: "11px", color: "#9AA0A6", fontStyle: "italic" }}>No recent stories</span></div>;
  }
  const topStories = stories.slice(0, 3);
  const extraCount = stories.length - 3;
  return (
    <div style={{ padding: "8px 14px" }}>
      {topStories.map((s, idx) => {
        const title = s.title || "";
        const display = title.length > 65 ? title.slice(0, 65) + "…" : title;
        return (
          <div key={s.brief_id} style={{ borderTop: idx > 0 ? "1px solid #F3F4F6" : "none", paddingTop: idx > 0 ? "5px" : 0, paddingBottom: "5px" }}>
            <button onClick={e => { e.stopPropagation(); navigate(`/story/${s.brief_id}`, { state: { story: s } }); }}
              style={{ display: "block", textAlign: "left", fontSize: "12px", fontWeight: 500, color: "#0A0A0A", fontFamily: "'Playfair Display', serif", lineHeight: "1.4", background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#97C8A6"; e.currentTarget.style.textDecoration = "underline"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#0A0A0A"; e.currentTarget.style.textDecoration = "none"; }}
              data-testid={`news-story-${s.brief_id}`}
            >{display}</button>
            <div style={{ fontSize: "10px", color: "#9AA0A6", marginTop: "1px" }}>{getRelativeTime(s.updated_at || s.first_seen_at)}</div>
          </div>
        );
      })}
      {extraCount > 0 && (
        <button onClick={e => { e.stopPropagation(); setShowModal(true); }}
          style={{ marginTop: "4px", fontSize: "11px", fontWeight: 500, color: "#97C8A6", background: "#E9F1EC", padding: "1px 8px", borderRadius: "999px", border: "none", cursor: "pointer" }}
          data-testid={`more-stories-${companyName}`}
        >
          +{extraCount} more
        </button>
      )}
      {showModal && <StoriesModal companyName={companyName} stories={stories} onClose={() => setShowModal(false)} />}
    </div>
  );
}

// ─── Confirm Company Modal ─────────────────────────────────────────────────────
function ConfirmCompanyModal({ companyName, onConfirm, onCancel }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const handleSubmit = () => {
    const parsed = parseWebsiteInput(value);
    if (!parsed) { setError("Please enter a domain or permalink."); return; }
    onConfirm(parsed);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onCancel}>
      <div className="bg-white rounded-md shadow-2xl p-6" style={{ maxWidth: "440px", width: "90%" }} onClick={e => e.stopPropagation()} data-testid="confirm-company-modal">
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 700, color: "#0A0A0A", marginBottom: "8px" }}>Confirm Company Details</h3>
        <p style={{ fontSize: "13px", color: "#6B7280", marginBottom: "16px", lineHeight: "1.6" }}>Please confirm the company's website domain or Crunchbase permalink for accurate news coverage.</p>
        <input type="text" value={value} onChange={e => { setValue(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && handleSubmit()}
          placeholder="e.g. apple.com or uber-china" autoFocus
          className="w-full h-10 px-3 rounded-md text-[13px] focus:outline-none mb-1.5"
          style={{ border: `1px solid ${error ? "#A63446" : "#DCDFE2"}`, background: "#FFFFFF" }}
          data-testid="confirm-company-input" />
        <p style={{ fontSize: "11px", color: error ? "#A63446" : "#9AA0A6", marginBottom: "20px" }}>
          {error || "Enter the full domain (apple.com) or Crunchbase permalink (openai, uber-china)"}
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-[13px] font-medium text-[#6B7280] hover:bg-[#F4F5F6] transition-colors" style={{ border: "1px solid #DCDFE2" }} data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={handleSubmit} disabled={!value.trim()} className="px-4 py-2 rounded-md text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity" style={{ background: "#3A3A3A" }} data-testid="confirm-generate-btn">Generate Now</button>
        </div>
      </div>
    </div>
  );
}

// ─── Coverage Cell ─────────────────────────────────────────────────────────────
function CoverageCell({ permalink, companyName, status: propStatus, reportId: propReportId, onStatusChange, externalTriggerRef }) {
  const navigate = useNavigate();
  const getInitialSnapshot = () => {
    const cachedData = getCompanyNews(permalink);
    const stored = getSearchedCompany(permalink);
    const rId = getStoredReportId(permalink) || stored?.reportId || propReportId || null;
    const sStatus = stored?.status || propStatus || null;
    if (cachedData?.articles?.length > 0) return { uiState: "cached", reportId: rId };
    if (!rId) return { uiState: "idle", reportId: null };
    if (["completed_not_fetched", "completed"].includes(sStatus)) return { uiState: "completed_not_fetched", reportId: rId };
    if (["queued", "processing", "starting", "delayed", "in_progress"].includes(sStatus)) return { uiState: "in_progress", reportId: rId };
    if (sStatus === "error") return { uiState: "error", reportId: rId };
    if (sStatus === "no_data_found") return { uiState: "no_data", reportId: rId };
    return { uiState: "idle", reportId: null };
  };
  const [uiState, setUiState] = useState(() => getInitialSnapshot().uiState);
  const [reportId, setReportId] = useState(() => getInitialSnapshot().reportId);
  const [statusLabel, setStatusLabel] = useState("Queued");
  const [fetching, setFetching] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [manualIdInput, setManualIdInput] = useState("");
  const [manualFetching, setManualFetching] = useState(false);
  const [zeroArticles, setZeroArticles] = useState(false);
  const pollRef = useRef(null);
  const mountRef = useRef(true);
  useEffect(() => { mountRef.current = true; return () => { mountRef.current = false; if (pollRef.current) clearInterval(pollRef.current); }; }, []);
  const stopPolling = useCallback(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, []);
  const checkStatus = useCallback(async (rId) => {
    if (!mountRef.current || !rId) return;
    try {
      const res = await fetch(`${API}/company-news/status/${rId}`);
      if (!res.ok || !mountRef.current) return;
      const data = await res.json();
      const s = (data.status || "processing").toLowerCase();
      if (s === "completed" || s === "exported") {
        stopPolling(); if (mountRef.current) { setUiState("completed_not_fetched"); setStatusLabel("Completed"); updateSearchedCompanyStatus(permalink, "completed_not_fetched", rId); onStatusChange?.(); }
      } else if (s === "no data found") {
        stopPolling(); if (mountRef.current) { setUiState("no_data"); updateSearchedCompanyStatus(permalink, "no_data_found", rId); onStatusChange?.(); }
      } else {
        if (mountRef.current) { setStatusLabel(s === "queued" ? "Queued" : s === "delayed" ? "Delayed" : "Processing"); updateSearchedCompanyStatus(permalink, s, rId); }
      }
    } catch {}
  }, [permalink, onStatusChange, stopPolling]);
  useEffect(() => {
    if (uiState === "in_progress" && reportId) { checkStatus(reportId); pollRef.current = setInterval(() => { if (mountRef.current) checkStatus(reportId); }, 10000); return () => stopPolling(); }
    stopPolling();
  }, [uiState, reportId, checkStatus, stopPolling]);
  const doGenerate = useCallback(async (cw) => {
    const stored = getSearchedCompany(permalink);
    if (!stored) addSearchedCompany(companyName, permalink, permalink, null, "starting");
    try {
      const res = await fetch(`${API}/company-news/start`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(buildStartPayload(cw)) });
      if (!res.ok || !mountRef.current) { setUiState("error"); updateSearchedCompanyStatus(permalink, "error"); return; }
      const data = await res.json();
      const rId = parseInt(data.report_id, 10);
      updateSearchedCompanyStatus(permalink, "queued", rId); storeReportId(permalink, rId);
      setReportId(rId); setStatusLabel("Queued"); setUiState("in_progress"); onStatusChange?.();
    } catch { if (mountRef.current) { setUiState("error"); updateSearchedCompanyStatus(permalink, "error"); } }
  }, [companyName, permalink, onStatusChange]);
  const doReset = useCallback(async (cw) => {
    setResetting(true); stopPolling(); clearCompanyNewsCacheEntry(permalink);
    try { localStorage.removeItem(`wokelo_report_id_${permalink}`); } catch {}
    addSearchedCompany(companyName, permalink, permalink, null, "starting");
    try {
      const res = await fetch(`${API}/company-news/start`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(buildStartPayload(cw)) });
      if (!res.ok) throw new Error("Start failed");
      const data = await res.json();
      const rId = parseInt(data.report_id, 10);
      updateSearchedCompanyStatus(permalink, "queued", rId); storeReportId(permalink, rId);
      setReportId(rId); setUiState("in_progress"); setStatusLabel("Queued"); setResetConfirm(false); onStatusChange?.();
    } catch { updateSearchedCompanyStatus(permalink, "error"); setUiState("error"); setResetConfirm(false); }
    finally { setResetting(false); }
  }, [companyName, permalink, onStatusChange, stopPolling]);
  const onGenerateClick = () => {
    const stored = getSearchedCompany(permalink);
    if (stored?.reportId || reportId || ["starting", "queued", "processing", "in_progress", "delayed"].includes(stored?.status)) return;
    const cw = getConfirmedWebsite(permalink);
    if (cw) doGenerate(cw); else { setPendingAction("generate"); setShowConfirmModal(true); }
  };
  if (externalTriggerRef) externalTriggerRef.current = onGenerateClick;
  const onResetConfirmed = () => { const cw = getConfirmedWebsite(permalink); if (cw) { setResetConfirm(false); doReset(cw); } else { setResetConfirm(false); setPendingAction("reset"); setShowConfirmModal(true); } };
  const onWebsiteConfirmed = (website) => { setConfirmedWebsite(permalink, website); setShowConfirmModal(false); if (pendingAction === "generate") doGenerate(website); else if (pendingAction === "reset") doReset(website); setPendingAction(null); };
  const doFetchNews = useCallback(async (rId) => {
    const id = parseInt(rId, 10); if (!id || isNaN(id)) return;
    const res = await fetch(`${API}/company-news/fetch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ report_id: id, page: 1, page_size: 100 }) });
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    return await res.json();
  }, []);
  const handleFetchNews = async () => {
    const rId = reportId || getStoredReportId(permalink);
    if (!rId || fetching) { setZeroArticles(true); return; }
    setFetching(true); setZeroArticles(false);
    try {
      const fd = await doFetchNews(rId);
      if (!mountRef.current) { setFetching(false); return; }
      const articles = fd?.data || [];
      if (articles.length === 0) { setFetching(false); setZeroArticles(true); return; }
      setCompanyNewsCache(permalink, articles, rId, companyName);
      updateSearchedCompanyStatus(permalink, "completed", rId);
      setUiState("cached"); setFetching(false); onStatusChange?.();
    } catch { if (mountRef.current) { setFetching(false); setUiState("error"); } }
  };
  const handleManualFetch = async () => {
    const rId = parseInt(manualIdInput.trim(), 10);
    if (!rId || isNaN(rId) || manualFetching) return;
    setManualFetching(true); setZeroArticles(false);
    try {
      const fd = await doFetchNews(rId);
      if (!mountRef.current) { setManualFetching(false); return; }
      const articles = fd?.data || [];
      if (articles.length === 0) { setManualFetching(false); setZeroArticles(true); return; }
      storeReportId(permalink, rId); setReportId(rId);
      setCompanyNewsCache(permalink, articles, rId, companyName);
      updateSearchedCompanyStatus(permalink, "completed", rId);
      setUiState("cached"); setManualFetching(false); onStatusChange?.();
    } catch { if (mountRef.current) setManualFetching(false); }
  };
  const ManualFetchPanel = (
    <div className="mt-1.5 pt-1.5" style={{ borderTop: "1px dashed #DCDFE2" }}>
      <p style={{ fontSize: "10px", color: "#9AA0A6", marginBottom: "4px" }}>Not fetching? Enter report ID manually:</p>
      <div className="flex items-center gap-1">
        <input type="text" value={manualIdInput} onChange={e => setManualIdInput(e.target.value.replace(/\D/g, ""))} placeholder={reportId ? String(reportId) : "e.g. 106447"} className="h-7 px-2 rounded text-[11px] focus:outline-none w-24" style={{ border: "1px solid #DCDFE2", background: "#FFFFFF" }} data-testid={`manual-report-id-${permalink}`} />
        <button onClick={handleManualFetch} disabled={!manualIdInput.trim() || manualFetching} className="h-7 px-2.5 rounded text-[11px] font-medium text-white transition-all hover:opacity-90 disabled:opacity-50" style={{ background: "#3A3A3A" }} data-testid={`manual-fetch-btn-${permalink}`}>{manualFetching ? <Loader2 size={10} className="animate-spin" /> : "Fetch →"}</button>
      </div>
    </div>
  );
  const ResetLink = <button onClick={() => setResetConfirm(true)} className="text-[10px] transition-colors self-start" style={{ color: "#9AA0A6" }} onMouseEnter={e => { e.currentTarget.style.color = "#6B7280"; }} onMouseLeave={e => { e.currentTarget.style.color = "#9AA0A6"; }} data-testid={`reset-link-${permalink}`}>↻ Reset</button>;
  const ResetModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }} onClick={() => setResetConfirm(false)}>
      <div className="bg-white rounded-md shadow-2xl p-6" style={{ maxWidth: "380px", width: "90%" }} onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Reset Coverage for {companyName}?</h3>
        <p className="text-[13px] text-[#6B7280] mb-5">Delete existing data and regenerate. Continue?</p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setResetConfirm(false)} className="px-4 py-2 rounded-md text-[13px] font-medium text-[#6B7280] hover:bg-[#F4F5F6]" style={{ border: "1px solid #DCDFE2" }}>Cancel</button>
          <button onClick={onResetConfirmed} disabled={resetting} className="px-4 py-2 rounded-md text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-60" style={{ background: "#A63446" }}>{resetting ? "Resetting…" : "Yes, Reset"}</button>
        </div>
      </div>
    </div>
  );
  const freshCache = getCompanyNews(permalink);
  if (uiState === "cached" || freshCache?.articles?.length > 0) {
    const count = freshCache?.articles?.length || 0;
    return (
      <div className="flex flex-col gap-1">
        {count > 0 ? <div className="flex items-center gap-3 flex-wrap"><button onClick={() => navigate(`/company-news?company=${encodeURIComponent(permalink)}`)} className="text-[13px] font-medium text-[#0A0A0A] hover:underline" data-testid={`view-coverage-${permalink}`}>View Coverage →</button><span className="text-[11px] text-[#9AA0A6]">{count} articles</span></div> : <div className="flex items-center gap-2"><button onClick={handleFetchNews} disabled={fetching} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-60" style={{ background: "#2A5B46" }} data-testid={`fetch-news-${permalink}`}>{fetching ? <><Loader2 size={10} className="animate-spin mr-1" />Fetching…</> : "Re-fetch →"}</button></div>}
        {zeroArticles && <p style={{ fontSize: "10px", color: "#A63446" }}>Report returned 0 articles.</p>}
        {ResetLink}{resetConfirm && ResetModal}
        {showConfirmModal && <ConfirmCompanyModal companyName={companyName} onConfirm={onWebsiteConfirmed} onCancel={() => { setShowConfirmModal(false); setPendingAction(null); }} />}
      </div>
    );
  }
  if (uiState === "in_progress") {
    return (
      <div className="flex flex-col gap-1.5">
        <button onClick={() => checkStatus(reportId || getStoredReportId(permalink))} className="px-3 py-1.5 rounded-md text-[11px] font-medium hover:bg-[#F4F5F6]" style={{ border: "1px solid #3A3A3A", color: "#3A3A3A", background: "#FFFFFF", width: "fit-content" }} data-testid={`check-status-${permalink}`}>Check Status</button>
        <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#F59E0B" }} /><span className="text-[11px] text-[#6B7280]">{statusLabel}…</span></div>
        {(reportId || getStoredReportId(permalink)) && <p style={{ fontSize: "10px", color: "#9AA0A6" }}>Report ID: <span style={{ color: "#3A3A3A", fontWeight: 600 }}>{reportId || getStoredReportId(permalink)}</span></p>}
        {ResetLink}{resetConfirm && ResetModal}
        {showConfirmModal && <ConfirmCompanyModal companyName={companyName} onConfirm={onWebsiteConfirmed} onCancel={() => { setShowConfirmModal(false); setPendingAction(null); }} />}
      </div>
    );
  }
  if (uiState === "completed_not_fetched") {
    return (
      <div className="flex flex-col gap-1.5">
        <button onClick={handleFetchNews} disabled={fetching} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-60" style={{ background: "#2A5B46", width: "fit-content" }} data-testid={`fetch-news-${permalink}`}>{fetching ? <><Loader2 size={10} className="animate-spin mr-1" />Fetching…</> : "Fetch News →"}</button>
        {reportId && <p style={{ fontSize: "10px", color: "#9AA0A6" }}>Report ID: <span style={{ color: "#3A3A3A", fontWeight: 600 }}>{reportId}</span></p>}
        {zeroArticles && <p style={{ fontSize: "10px", color: "#A63446" }}>Report returned 0 articles.</p>}
        {ManualFetchPanel}{ResetLink}{resetConfirm && ResetModal}
        {showConfirmModal && <ConfirmCompanyModal companyName={companyName} onConfirm={onWebsiteConfirmed} onCancel={() => { setShowConfirmModal(false); setPendingAction(null); }} />}
      </div>
    );
  }
  if (uiState === "error") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-[12px] text-[#A63446]">Error</span>
        <button onClick={onGenerateClick} className="text-[11px] text-[#6B7280] hover:text-[#0A0A0A] underline">Retry</button>
        {showConfirmModal && <ConfirmCompanyModal companyName={companyName} onConfirm={onWebsiteConfirmed} onCancel={() => { setShowConfirmModal(false); setPendingAction(null); }} />}
      </div>
    );
  }
  if (uiState === "no_data") return <span className="text-[12px] text-[#9AA0A6] italic">No data found</span>;
  return <>{showConfirmModal && <ConfirmCompanyModal companyName={companyName} onConfirm={onWebsiteConfirmed} onCancel={() => { setShowConfirmModal(false); setPendingAction(null); }} />}</>;
}

// ─── Column config ─────────────────────────────────────────────────────────────
const COLS = [
  { label: "Company",         w: "220px", frozen: true  },
  { label: "Latest News",     w: "300px", frozen: false },
  { label: "Flagged",         w: "80px",  frozen: false },
  { label: "Valuation",       w: "160px", frozen: false },
  { label: "Revenue Trend",   w: "160px", frozen: false },
  { label: "Headcount",       w: "160px", frozen: false },
  { label: "Web Traffic",     w: "140px", frozen: false },
  { label: "LinkedIn",        w: "120px", frozen: false },
  { label: "Glassdoor",       w: "110px", frozen: false },
  { label: "News Articles",   w: "180px", frozen: false },
  { label: "Website",         w: "150px", frozen: false },
];
const TABLE_MIN_W = "1800px";

const TH_BASE = {
  padding: "9px 12px", fontSize: "9px", fontWeight: 700,
  color: "#9AA0A6", letterSpacing: "0.07em", textTransform: "uppercase",
  background: "#FAFBFC", whiteSpace: "nowrap", borderBottom: "1px solid #DCDFE2",
};

// Task 4: loadingStories spinner on Latest News header
function TableHeader({ loadingStories = false }) {
  return (
    <tr>
      {COLS.map(col => (
        <th key={col.label} style={{ ...TH_BASE, width: col.w, minWidth: col.w, ...(col.frozen ? { position: "sticky", left: 0, zIndex: 30, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" } : {}) }}>
          {col.label === "Latest News" && loadingStories
            ? <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>{col.label} <Loader2 size={9} className="animate-spin" /></div>
            : col.label}
        </th>
      ))}
    </tr>
  );
}

// ─── Portfolio Row ──────────────────────────────────────────────────────────────
// ─── Company Action Modal ─────────────────────────────────────────────────────
function CompanyActionModal({ company, seedRow, onClose, onFetchSuccess }) {
  const [tab, setTab] = useState("fetch");
  const [reportIdInput, setReportIdInput] = useState(() => {
    const v = getStoredReportId(company.permalink);
    return v ? String(v) : "";
  });
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);

  const [websiteInput, setWebsiteInput] = useState(
    getConfirmedWebsite(company.permalink) || seedRow?.website || ""
  );
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const doFetch = async () => {
    const rId = parseInt(reportIdInput.trim(), 10);
    if (!rId || isNaN(rId) || fetching) return;
    setFetching(true); setFetchResult(null);
    try {
      const res = await fetch(`${API}/company-news/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report_id: rId, page: 1, page_size: 100 }),
      });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      const articles = data?.data || [];
      if (articles.length === 0) {
        setFetchResult({ ok: false, count: 0 });
      } else {
        storeReportId(company.permalink, rId);
        setCompanyNewsCache(company.permalink, articles, rId, company.name);
        // Ensure the company exists in searched list (addSearchedCompany upserts)
        addSearchedCompany(company.name, company.permalink, company.permalink, rId, "completed");
        setFetchResult({ ok: true, count: articles.length });
        setTimeout(() => { onFetchSuccess?.(); onClose(); }, 1200);
      }
    } catch (e) {
      setFetchResult({ ok: false, error: e.message });
    } finally {
      setFetching(false);
    }
  };

  const doGenerate = async () => {
    const cw = parseWebsiteInput(websiteInput);
    if (!cw || generating) return;
    setGenerating(true); setGenResult(null);
    setConfirmedWebsite(company.permalink, cw);
    if (!getSearchedCompany(company.permalink)) {
      addSearchedCompany(company.name, company.permalink, company.permalink, null, "starting");
    }
    try {
      const res = await fetch(`${API}/company-news/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildStartPayload(cw)),
      });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      const rId = parseInt(data.report_id, 10);
      storeReportId(company.permalink, rId);
      updateSearchedCompanyStatus(company.permalink, "queued", rId);
      setReportIdInput(String(rId));
      setGenResult({ ok: true, reportId: rId });
    } catch (e) {
      setGenResult({ ok: false, error: e.message });
    } finally {
      setGenerating(false);
    }
  };

  const tabStyle = (id) => ({
    flex: 1, padding: "10px 16px", fontSize: "12px",
    fontWeight: tab === id ? 600 : 400,
    color: tab === id ? "#97C8A6" : "#6B7280",
    borderBottom: tab === id ? "2px solid #97C8A6" : "2px solid transparent",
    background: "none", border: "none",
    cursor: "pointer", transition: "all 150ms",
  });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}>
      <div style={{ background: "#FFF", borderRadius: "6px", boxShadow: "0 8px 40px rgba(0,0,0,0.20)", width: "460px", maxWidth: "95vw", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #ECEEF0", display: "flex", alignItems: "center", gap: "12px" }}>
          <CompanyLogo name={company.name} permalink={company.permalink} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0A0A0A", margin: 0, fontFamily: "'Playfair Display', serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{company.name}</h3>
            {seedRow?.sector && <div style={{ fontSize: "11px", color: "#9AA0A6", marginTop: "1px" }}>{seedRow.sector}</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9AA0A6", padding: "4px", borderRadius: "6px" }}>
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #ECEEF0" }}>
          <button style={tabStyle("fetch")} onClick={() => setTab("fetch")} data-testid={`modal-tab-fetch-${company.permalink}`}>Fetch by Report ID</button>
          <button style={tabStyle("generate")} onClick={() => setTab("generate")} data-testid={`modal-tab-generate-${company.permalink}`}>Generate New Report</button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px 20px 24px" }}>
          {tab === "fetch" ? (
            <>
              <p style={{ fontSize: "13px", color: "#6B7280", marginBottom: "16px", lineHeight: 1.5 }}>
                Enter a Report ID from an existing Wokelo news report to load its articles directly into the portfolio table.
              </p>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#9AA0A6", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: "6px" }}>Report ID</label>
              <input
                type="text"
                value={reportIdInput}
                onChange={e => setReportIdInput(e.target.value.replace(/\D/g, ""))}
                onKeyDown={e => e.key === "Enter" && doFetch()}
                placeholder="e.g. 106447"
                autoFocus
                style={{ width: "100%", height: "42px", padding: "0 14px", borderRadius: "6px", border: "1.5px solid #DCDFE2", fontSize: "14px", background: "#FFFFFF", outline: "none", boxSizing: "border-box", transition: "border-color 150ms" }}
                onFocus={e => { e.target.style.borderColor = "#97C8A6"; }}
                onBlur={e => { e.target.style.borderColor = "#DCDFE2"; }}
                data-testid={`modal-report-id-input-${company.permalink}`}
              />
              {fetchResult && (
                <div style={{ marginTop: "10px", fontSize: "12px", padding: "10px 14px", borderRadius: "6px",
                  background: fetchResult.ok ? "#DCFCE7" : fetchResult.count === 0 ? "#FEF3C7" : "#FEE2E2",
                  color: fetchResult.ok ? "#15803D" : fetchResult.count === 0 ? "#92400E" : "#DC2626",
                  fontWeight: 500 }}>
                  {fetchResult.ok
                    ? `${fetchResult.count} articles fetched — closing…`
                    : fetchResult.count === 0
                    ? "Report returned 0 articles. Try a different Report ID."
                    : `Error: ${fetchResult.error}`}
                </div>
              )}
              <button onClick={doFetch} disabled={!reportIdInput.trim() || fetching}
                style={{ marginTop: "14px", width: "100%", height: "42px", borderRadius: "6px", border: "none",
                  background: !reportIdInput.trim() || fetching ? "#E5E7EB" : "#0A0A0A",
                  color: !reportIdInput.trim() || fetching ? "#9CA3AF" : "#FFF",
                  fontSize: "13px", fontWeight: 600, cursor: !reportIdInput.trim() || fetching ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                data-testid={`modal-fetch-btn-${company.permalink}`}>
                {fetching ? <><Loader2 size={14} className="animate-spin" /> Fetching…</> : "Fetch Articles →"}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: "13px", color: "#6B7280", marginBottom: "16px", lineHeight: 1.5 }}>
                Start a new Wokelo news report for this company. Generation takes ~2–3 minutes. You'll receive a Report ID to fetch articles once complete.
              </p>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#9AA0A6", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: "6px" }}>Company Website or Permalink</label>
              <input
                type="text"
                value={websiteInput}
                onChange={e => setWebsiteInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && doGenerate()}
                placeholder="e.g. openai.com or openai"
                style={{ width: "100%", height: "42px", padding: "0 14px", borderRadius: "6px", border: "1.5px solid #DCDFE2", fontSize: "14px", background: "#FFFFFF", outline: "none", boxSizing: "border-box", transition: "border-color 150ms" }}
                onFocus={e => { e.target.style.borderColor = "#97C8A6"; }}
                onBlur={e => { e.target.style.borderColor = "#DCDFE2"; }}
                data-testid={`modal-website-input-${company.permalink}`}
              />
              {genResult && (
                <div style={{ marginTop: "10px", fontSize: "12px", padding: "10px 14px", borderRadius: "6px",
                  background: genResult.ok ? "#DBEAFE" : "#FEE2E2",
                  color: genResult.ok ? "#1D4ED8" : "#DC2626", fontWeight: 500 }}>
                  {genResult.ok ? (
                    <span>Report started! Report ID: <strong>{genResult.reportId}</strong> —{" "}
                      <button onClick={() => setTab("fetch")} style={{ color: "#1D4ED8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, fontSize: "12px", fontWeight: 600 }}>
                        Fetch when ready →
                      </button>
                    </span>
                  ) : `Error: ${genResult.error}`}
                </div>
              )}
              <button onClick={doGenerate} disabled={!websiteInput.trim() || generating}
                style={{ marginTop: "14px", width: "100%", height: "42px", borderRadius: "6px", border: "none",
                  background: !websiteInput.trim() || generating ? "#E5E7EB" : "#97C8A6",
                  color: !websiteInput.trim() || generating ? "#9CA3AF" : "#181B20",
                  fontSize: "13px", fontWeight: 600, cursor: !websiteInput.trim() || generating ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                data-testid={`modal-generate-btn-${company.permalink}`}>
                {generating ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : "Generate Report →"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PortfolioRow({ company, stories, seedRow, handleStatusChange, isFlashing, rowRef, flaggedCompanies }) {
  const navigate = useNavigate();
  const [showActionModal, setShowActionModal] = useState(false);
  const allStories = useMemo(() => getTopStories(stories, company.name), [stories, company.name]);
  const liveSentiment = useMemo(() => {
    const imp = stories.flatMap(s => s.portfolio_impact || []).find(p => p.company_name === company.name);
    return imp?.sentiment || null;
  }, [stories, company.name]);

  const handleNameClick = () => {
    const foundPermalink = hasCompanyNewsDataEx(company.permalink, company.name);
    if (foundPermalink) {
      navigate(`/company-news?company=${encodeURIComponent(foundPermalink)}`);
    } else {
      setShowActionModal(true);
    }
  };

  // Clear ALL cached data for this company (all key variants)
  const handleClearCache = () => {
    const seedWebsite = PORTFOLIO_SEED[company.permalink]?.website || "";
    const keysToRemove = [company.permalink, deriveNamePermalink(company.name || "")];
    if (seedWebsite) {
      keysToRemove.push(deriveNamePermalink(seedWebsite));
      const stem = seedWebsite.replace(/\.[^.]+$/, "");
      if (stem) keysToRemove.push(deriveNamePermalink(stem));
    }
    const seen = new Set();
    keysToRemove.forEach(k => {
      if (!k || seen.has(k)) return;
      seen.add(k);
      clearCompanyNewsCacheEntry(k);
      try { localStorage.removeItem(`wokelo_report_id_${k}`); } catch {}
      try { localStorage.removeItem(`wokelo_confirmed_website_${k}`); } catch {}
      removeSearchedCompany(k);
    });
    handleStatusChange();
  };

  const displaySentiment = liveSentiment || seedRow?.sentiment || null;
  return (
    <>
    {showActionModal && (
      <CompanyActionModal
        company={company}
        seedRow={seedRow}
        onClose={() => setShowActionModal(false)}
        onFetchSuccess={() => { handleStatusChange(); setShowActionModal(false); }}
      />
    )}
    <tr ref={rowRef} className={isFlashing ? "flash-animate" : ""} style={{ borderBottom: "1px solid #F0F2F4", transition: "background-color 120ms" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#F9FAF9"; }}
      onMouseLeave={e => { e.currentTarget.style.background = ""; }}
      data-testid={`portfolio-row-${company.name}`}
    >
      <td style={{ position: "sticky", left: 0, zIndex: 10, background: "white", boxShadow: "2px 0 4px rgba(0,0,0,0.06)", padding: "10px 14px", minWidth: "220px", width: "220px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <CompanyLogo name={company.name} permalink={company.permalink} size={24} />
          <button onClick={handleNameClick} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }} data-testid={`company-name-trigger-${company.permalink}`}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A", lineHeight: 1.3 }}
                onMouseEnter={e => { e.currentTarget.style.color = "#97C8A6"; e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#0A0A0A"; e.currentTarget.style.textDecoration = "none"; }}>
                {company.name}
              </span>
              {seedRow?.flag && <span style={{ fontSize: "12px", lineHeight: 1 }}>{seedRow.flag}</span>}
            </div>
            {seedRow?.sector && <div style={{ fontSize: "10px", color: "#9AA0A6", marginTop: "1px" }}>{seedRow.sector}</div>}
          </button>
        </div>
      </td>
      <td style={{ minWidth: "300px", width: "300px", verticalAlign: "top" }}><LatestNewsCell stories={allStories} companyName={company.name} /></td>
      <td style={{ minWidth: "80px", width: "80px", verticalAlign: "top" }}><FlaggedCell companyId={company.permalink} flaggedCompanies={flaggedCompanies} /></td>
      <td style={{ minWidth: "160px", width: "160px", verticalAlign: "top" }}><ValuationCell seedRow={seedRow} /></td>
      <td style={{ minWidth: "160px", width: "160px", verticalAlign: "top" }}><RevenueTrendCell seedRow={seedRow} /></td>
      <td style={{ minWidth: "160px", width: "160px", verticalAlign: "top" }}><HeadcountCell seedRow={seedRow} /></td>
      <td style={{ minWidth: "140px", width: "140px", verticalAlign: "top" }}><WebTrafficCell seedRow={seedRow} /></td>
      <td style={{ minWidth: "120px", width: "120px", verticalAlign: "top" }}><LinkedInCell seedRow={seedRow} /></td>
      <td style={{ minWidth: "110px", width: "110px", verticalAlign: "top" }}><GlassdoorCell seedRow={seedRow} /></td>
      <td style={{ minWidth: "180px", width: "180px", verticalAlign: "top" }}><NewsArticlesCell permalink={company.permalink} companyName={company.name} topStories={allStories} onFetchNewsClick={() => setShowActionModal(true)} onClearCache={handleClearCache} /></td>
      <td style={{ minWidth: "150px", width: "150px", verticalAlign: "top" }}><WebsiteCell seedRow={seedRow} /></td>
    </tr>
    </>
  );
}

// ─── Searched Row ──────────────────────────────────────────────────────────────
const SEARCHED_EXTRA_WIDTHS = [160, 160, 160, 140, 120, 110, 120, 180, 150];

function SearchedRow({ company, stories, handleStatusChange }) {
  const navigate = useNavigate();
  const coverageRef = useRef(null);
  const cacheKey = company.permalink || company.domain;
  const displayName = formatDisplayName(company);
  const allStories = useMemo(() => getTopStories(stories, displayName), [stories, displayName]);

  const handleNameClick = () => {
    const foundPermalink = hasCompanyNewsDataEx(cacheKey, displayName);
    if (foundPermalink) navigate(`/company-news?company=${encodeURIComponent(foundPermalink)}`);
    else coverageRef.current?.();
  };

  return (
    <tr style={{ borderBottom: "1px solid #F0F2F4", background: "#FFFFFF", transition: "background-color 120ms" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#E9F1EC"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#FFFFFF"; }}
      data-testid={`searched-row-${cacheKey}`}
    >
      <td style={{ position: "sticky", left: 0, zIndex: 10, background: "inherit", boxShadow: "2px 0 4px rgba(0,0,0,0.06)", padding: "10px 14px", minWidth: "220px", width: "220px" }}>
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <CompanyLogo name={displayName} permalink={cacheKey} size={24} />
            <button onClick={handleNameClick} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#97C8A6"; e.currentTarget.style.textDecoration = "underline"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#0A0A0A"; e.currentTarget.style.textDecoration = "none"; }}
                >{displayName}</span>
                <span style={{ fontSize: "9px", fontWeight: 600, padding: "1px 5px", borderRadius: "3px", background: "#DCDFE2", color: "#6B7280" }}>Searched</span>
              </div>
              <div style={{ fontSize: "10px", color: "#9AA0A6", marginTop: "1px" }}>Added {getRelativeTime(company.addedAt)}</div>
            </button>
          </div>
          <div style={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden", top: 0, left: 0 }}>
            <CoverageCell permalink={cacheKey} companyName={displayName} status={company.status} reportId={company.reportId} onStatusChange={handleStatusChange} externalTriggerRef={coverageRef} />
          </div>
        </div>
      </td>
      <td style={{ minWidth: "300px", width: "300px", verticalAlign: "top" }}><LatestNewsCell stories={allStories} companyName={displayName} /></td>
      {SEARCHED_EXTRA_WIDTHS.map((w, i) => (
        <td key={i} style={{ minWidth: `${w}px`, width: `${w}px`, padding: "12px", verticalAlign: "top" }}>{DASH}</td>
      ))}
    </tr>
  );
}

// ─── Skeleton Row ──────────────────────────────────────────────────────────────
const SKELETON_EXTRA_WIDTHS = [300, 160, 160, 160, 140, 120, 110, 120, 180, 150];
function SkeletonRow() {
  return (
    <tr style={{ borderBottom: "1px solid #F0F2F4" }}>
      <td style={{ position: "sticky", left: 0, zIndex: 10, background: "white", padding: "12px 14px", minWidth: "220px", boxShadow: "2px 0 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <div style={{ width: 32, height: 32, borderRadius: 6, background: "#ECEEF0" }} className="animate-pulse" />
          <div><div style={{ height: 12, width: 100, background: "#ECEEF0", borderRadius: 4, marginBottom: 5 }} className="animate-pulse" /><div style={{ height: 9, width: 70, background: "#F4F5F6", borderRadius: 4 }} className="animate-pulse" /></div>
        </div>
      </td>
      {SKELETON_EXTRA_WIDTHS.map((w, i) => (
        <td key={i} style={{ minWidth: `${w}px`, padding: "12px", verticalAlign: "top" }}>
          <div style={{ height: 10, width: "70%", background: "#ECEEF0", borderRadius: 4, marginBottom: 5 }} className="animate-pulse" />
          <div style={{ height: 8, width: "50%", background: "#F4F5F6", borderRadius: 4 }} className="animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

// ─── Task 4: Filter Bar ────────────────────────────────────────────────────────
const PERIOD_DAYS = { today: 0, yesterday: 1, "3days": 3, "7days": 7 };

const SELECT_BASE = (active) => ({
  fontSize: "11px", padding: "4px 22px 4px 8px", borderRadius: "6px",
  border: `1px solid ${active ? "#6366F1" : "#DCDFE2"}`,
  background: active ? "#EEF2FF" : "#FFFFFF",
  color: active ? "#4338CA" : "#3A3A3A",
  cursor: "pointer", appearance: "none", WebkitAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239AA0A6' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 6px center", backgroundSize: "10px",
  minWidth: "80px",
});

const LABEL_STYLE = { fontSize: "10px", fontWeight: 700, color: "#9AA0A6", letterSpacing: "0.07em", textTransform: "uppercase", whiteSpace: "nowrap", flexShrink: 0 };
const DIV = { width: "1px", height: "20px", background: "#E5E7EB", flexShrink: 0 };

const STORIES_OPTS = [
  { v: "All",          activeBg: "#3A3A3A", activeC: "#FFFFFF", activeBd: "#3A3A3A" },
  { v: "Latest News",  activeBg: "#E9F1EC", activeC: "#2A5B46", activeBd: "#97C8A6" },
];

function FilterBar({ sectorFilter, onSectorChange, countryFilter, onCountryChange, countryOptions, storiesFilter, onStoriesChange, flaggedFilter, onFlaggedChange, period, onPeriodChange, loadingStories, onRefresh }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", overflowX: "auto", paddingBottom: "4px" }}>
      <span style={LABEL_STYLE}>Sector:</span>
      {SECTOR_CATEGORIES.map(s => {
        const active = sectorFilter === s;
        return (
          <button key={s} onClick={() => onSectorChange(s)} data-testid={`sector-filter-${s.toLowerCase().replace(/[\s/]+/g, "-")}`}
            style={{ fontSize: "11px", fontWeight: 500, padding: "4px 10px", borderRadius: "6px", border: "1px solid #97C8A6", background: active ? "#97C8A6" : "#FFFFFF", color: active ? "#181B20" : "#97C8A6", cursor: "pointer", transition: "all 120ms", whiteSpace: "nowrap", flexShrink: 0 }}>
            {s}
          </button>
        );
      })}
      <div style={DIV} />
      <span style={LABEL_STYLE}>Country:</span>
      <select value={countryFilter} onChange={e => onCountryChange(e.target.value)} style={SELECT_BASE(countryFilter !== "All")} data-testid="country-filter">
        {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <div style={DIV} />
      <span style={LABEL_STYLE}>Stories:</span>
      <div style={{ display: "flex", flexShrink: 0 }}>
        {STORIES_OPTS.map((opt, idx) => {
          const active = storiesFilter === opt.v;
          return (
            <button key={opt.v} onClick={() => onStoriesChange(opt.v)} data-testid={`stories-filter-${opt.v.toLowerCase().replace(/\s+/g, "-")}`}
              style={{ fontSize: "10px", fontWeight: 500, padding: "4px 9px", borderTop: "1px solid", borderBottom: "1px solid", borderLeft: idx === 0 ? "1px solid" : "none", borderRight: "1px solid", borderColor: active ? opt.activeBd : "#DCDFE2", background: active ? opt.activeBg : "#FFFFFF", color: active ? opt.activeC : "#6B7280", cursor: "pointer", borderRadius: idx === 0 ? "6px 0 0 6px" : "0 6px 6px 0", whiteSpace: "nowrap" }}>
              {opt.v}
            </button>
          );
        })}
      </div>
      <div style={DIV} />
      <span style={LABEL_STYLE}>Flagged:</span>
      <div style={{ display: "flex", flexShrink: 0 }}>
        {[{ v: "All", label: "All" }, { v: "Yes", label: "Yes" }, { v: "No", label: "No" }].map((opt, idx) => {
          const active = flaggedFilter === opt.v;
          const isYes = opt.v === "Yes";
          return (
            <button key={opt.v} onClick={() => onFlaggedChange(opt.v)} data-testid={`flagged-filter-${opt.v.toLowerCase()}`}
              style={{ 
                fontSize: "10px", fontWeight: 500, padding: "4px 9px", 
                borderTop: "1px solid", borderBottom: "1px solid", 
                borderLeft: idx === 0 ? "1px solid" : "none", borderRight: "1px solid", 
                borderColor: active ? (isYes ? "#16A34A" : "#DCDFE2") : "#DCDFE2", 
                background: active ? (isYes ? "#DCFCE7" : "#F3F4F6") : "#FFFFFF", 
                color: active ? (isYes ? "#16A34A" : "#374151") : "#6B7280", 
                cursor: "pointer", 
                borderRadius: idx === 0 ? "6px 0 0 6px" : idx === 2 ? "0 6px 6px 0" : "0", 
                whiteSpace: "nowrap" 
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>
      <div style={DIV} />
      <span style={LABEL_STYLE}>Period:</span>
      <select value={period} onChange={e => onPeriodChange(e.target.value)} style={SELECT_BASE(period !== "7days")} data-testid="period-filter">
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="3days">Last 3 Days</option>
        <option value="7days">Last 7 Days</option>
      </select>
      <div style={{ marginLeft: "auto", flexShrink: 0 }}>
        <button onClick={onRefresh} disabled={loadingStories}
          style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#9AA0A6", background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: "6px", transition: "color 120ms" }}
          onMouseEnter={e => { e.currentTarget.style.color = "#97C8A6"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#9AA0A6"; }}
          data-testid="portfolio-refresh-btn"
        >
          <RefreshCw size={12} className={loadingStories ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function PortfolioMatrix() {
  const [searchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStories, setLoadingStories] = useState(false);
  const [dbOverrides, setDbOverrides] = useState({});
  const [searchedCos, setSearchedCos] = useState([]);
  const [flashCompany, setFlashCompany] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [flaggedCompanies, setFlaggedCompanies] = useState([]);
  const rowRefs = useRef({});

  // Filter state (Task 4)
  const [sectorFilter, setSectorFilter] = useState(() => { try { return localStorage.getItem("wokelo_portfolio_sector") || "All"; } catch { return "All"; } });
  const [countryFilter, setCountryFilter] = useState(() => { try { return localStorage.getItem("wokelo_portfolio_country") || "All"; } catch { return "All"; } });
  const [storiesFilter, setStoriesFilter] = useState(() => { try { const v = localStorage.getItem("wokelo_portfolio_stories") || "All"; return ["All", "Latest News"].includes(v) ? v : "All"; } catch { return "All"; } });
  const [flaggedFilter, setFlaggedFilter] = useState(() => { try { return localStorage.getItem("wokelo_portfolio_flagged") || "All"; } catch { return "All"; } });
  const [period, setPeriod] = useState(() => { try { return localStorage.getItem("wokelo_portfolio_period") || "7days"; } catch { return "7days"; } });

  const handleSectorChange = (s) => { setSectorFilter(s); try { localStorage.setItem("wokelo_portfolio_sector", s); } catch {} };
  const handleCountryChange = (c) => { setCountryFilter(c); try { localStorage.setItem("wokelo_portfolio_country", c); } catch {} };
  const handleStoriesChange = (s) => { setStoriesFilter(s); try { localStorage.setItem("wokelo_portfolio_stories", s); } catch {} };
  const handleFlaggedChange = (v) => { setFlaggedFilter(v); try { localStorage.setItem("wokelo_portfolio_flagged", v); } catch {} };

  // Country options from seed data
  const countryOptions = useMemo(() => {
    const seen = new Set();
    Object.values(PORTFOLIO_SEED).forEach(s => { if (s.country) seen.add(s.country); });
    return ["All", ...[...seen].sort()];
  }, []);

  // Task 4: Period-based story fetch
  const fetchStoriesForPeriod = useCallback(async (days, forceRefresh = false) => {
    const cachedStories = !forceRefresh ? getAllCachedStories() : [];
    if (cachedStories.length > 0) { setStories(cachedStories); return; }
    setLoadingStories(true);
    try {
      const after = format(startOfDay(subDays(new Date(), days)), "yyyy-MM-dd'T'HH:mm:ss");
      const res = await fetch(`${API}/stories/bulk?updated_after=${after}&max_stories=500`);
      if (res.ok) { const data = await res.json(); const list = data.data || []; setStories(list); setNewsStoriesCache(format(new Date(), "yyyy-MM-dd"), list); }
    } catch (e) { console.error(e); }
    finally { setLoadingStories(false); }
  }, []);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    try { localStorage.setItem("wokelo_portfolio_period", newPeriod); } catch {}
    fetchStoriesForPeriod(PERIOD_DAYS[newPeriod], true);
  };
  const fetchDbOverrides = useCallback(async () => {
    try {
      const res = await fetch(`${API}/portfolio-intel`);
      if (res.ok) { const data = await res.json(); setDbOverrides(data); }
    } catch { /* silent fallback to seed */ }
  }, []);

  const fetchFlaggedCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/flagged`);
      if (res.ok) { 
        const data = await res.json(); 
        setFlaggedCompanies(data.flagged_companies || []); 
      }
    } catch (e) { 
      console.error("Failed to fetch flagged companies:", e);
    }
  }, []);

  const handleRefresh = () => {
    fetchStoriesForPeriod(PERIOD_DAYS[period], true);
    fetchDbOverrides();
    fetchFlaggedCompanies();
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStoriesForPeriod(PERIOD_DAYS[period]), fetchDbOverrides(), fetchFlaggedCompanies()]).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setSearchedCos(getSearchedCompanies()); }, [refreshKey]);

  // Task 1: All companies from PORTFOLIO_SEED
  const portfolioCompanies = useMemo(() => {
    const map = new Map(Object.keys(PORTFOLIO_SEED).map(k => [k, { name: PORTFOLIO_SEED[k].name, permalink: k, storyCount: 0, highImpactCount: 0 }]));
    stories.forEach(story => {
      (story.portfolio_impact || []).forEach(imp => {
        let entry = map.get(imp.company_permalink);
        if (!entry) { for (const [, v] of map) { if (v.name.toLowerCase() === (imp.company_name || "").toLowerCase()) { entry = v; break; } } }
        if (entry) { entry.storyCount++; if (imp.scale === "high") entry.highImpactCount++; }
      });
    });
    return Array.from(map.values()).sort((a, b) => b.storyCount - a.storyCount || a.name.localeCompare(b.name));
  }, [stories]);

  const portfolioNames = useMemo(() => new Set(portfolioCompanies.map(c => c.name.toLowerCase())), [portfolioCompanies]);
  const filteredSearchedCos = useMemo(() => searchedCos.filter(c => !portfolioNames.has((c.name || "").toLowerCase())), [searchedCos, portfolioNames]);

  // Task 4: Combined 4-filter logic + flagged filter
  const flaggedCompanyIds = useMemo(() => new Set(flaggedCompanies.map(fc => fc.company_id)), [flaggedCompanies]);
  
  const filteredPortfolioCompanies = useMemo(() => {
    let result = portfolioCompanies;
    if (sectorFilter !== "All") result = result.filter(c => PORTFOLIO_SEED[c.permalink]?.sector_category === sectorFilter);
    if (countryFilter !== "All") result = result.filter(c => PORTFOLIO_SEED[c.permalink]?.country === countryFilter);
    if (storiesFilter === "Latest News") result = result.filter(c => c.storyCount > 0);
    if (flaggedFilter === "Yes") result = result.filter(c => flaggedCompanyIds.has(c.permalink));
    if (flaggedFilter === "No") result = result.filter(c => !flaggedCompanyIds.has(c.permalink));
    return result;
  }, [portfolioCompanies, sectorFilter, countryFilter, storiesFilter, flaggedFilter, flaggedCompanyIds]);

  // Scroll + flash on ?company= param
  useEffect(() => {
    if (loading || portfolioCompanies.length === 0) return;
    const p = searchParams.get("company");
    if (!p) return;
    const ref = rowRefs.current[p];
    if (ref) { setTimeout(() => { ref.scrollIntoView({ behavior: "smooth", block: "center" }); setFlashCompany(p); setTimeout(() => setFlashCompany(null), 700); }, 100); }
  }, [loading, portfolioCompanies, searchParams]);

  const handleStatusChange = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `@keyframes rowFlash{0%{background-color:transparent}40%{background-color:rgba(166,52,70,0.10)}100%{background-color:transparent}}.flash-animate{animation:rowFlash 700ms ease-out forwards}`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const activeFilters = [sectorFilter !== "All" && `Sector: ${sectorFilter}`, countryFilter !== "All" && `Country: ${countryFilter}`, storiesFilter !== "All" && storiesFilter, flaggedFilter !== "All" && `Flagged: ${flaggedFilter}`].filter(Boolean);

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100%" }}>
      <div style={{ padding: "24px 32px" }}>

        {/* Header */}
        <div className="mb-4">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "32px", fontWeight: 700, color: "#0A0A0A" }}>Portfolio Matrix</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Track intelligence across {portfolioCompanies.length} portfolio companies</p>
        </div>

        {/* Task 4: Filter bar */}
        <div style={{ marginBottom: "16px", background: "#FFFFFF", borderRadius: "6px", padding: "10px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #DCDFE2" }}>
          <FilterBar
            sectorFilter={sectorFilter} onSectorChange={handleSectorChange}
            countryFilter={countryFilter} onCountryChange={handleCountryChange} countryOptions={countryOptions}
            storiesFilter={storiesFilter} onStoriesChange={handleStoriesChange}
            flaggedFilter={flaggedFilter} onFlaggedChange={handleFlaggedChange}
            period={period} onPeriodChange={handlePeriodChange}
            loadingStories={loadingStories} onRefresh={handleRefresh}
          />
        </div>

        {/* Portfolio table */}
        <div className="bg-white rounded-md overflow-hidden mb-8" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.10)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #DCDFE2", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Building2 size={15} style={{ color: "#3A3A3A" }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A" }}>Your Portfolio</span>
            {!loading && <span style={{ fontSize: "11px", color: "#9AA0A6" }}>· {filteredPortfolioCompanies.length} of {portfolioCompanies.length} companies</span>}
            {activeFilters.map(f => (
              <span key={f} style={{ fontSize: "10px", fontWeight: 600, padding: "2px 8px", borderRadius: "999px", background: "#F3F4F6", color: "#6B7280" }}>{f}</span>
            ))}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: TABLE_MIN_W, borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
              <thead><TableHeader loadingStories={loadingStories} /></thead>
              <tbody>
                {loading ? [...Array(6)].map((_, i) => <SkeletonRow key={i} />) :
                  filteredPortfolioCompanies.length === 0 ? (
                    <tr><td colSpan={COLS.length} style={{ padding: "48px 20px", textAlign: "center" }}>
                      <p style={{ fontSize: "13px", color: "#9AA0A6", fontStyle: "italic" }}>No companies match the current filters</p>
                    </td></tr>
                  ) : (
                    filteredPortfolioCompanies.map(company => {
                      const seedRow = { ...(PORTFOLIO_SEED[company.permalink] || {}), ...(dbOverrides[company.permalink] || {}) };
                      return (
                        <PortfolioRow key={company.name} company={company} stories={stories} seedRow={seedRow}
                          handleStatusChange={handleStatusChange} isFlashing={flashCompany === company.name}
                          rowRef={el => { rowRefs.current[company.name] = el; }} flaggedCompanies={flaggedCompanies} />
                      );
                    })
                  )
                }
              </tbody>
            </table>
          </div>
        </div>

        {/* Companies You Searched */}
        {filteredSearchedCos.length > 0 && (
          <div className="rounded-md overflow-hidden" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.10)", background: "#FFFFFF" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #DCDFE2", background: "#F4F5F6", display: "flex", alignItems: "center", gap: "8px" }}>
              <Search size={14} style={{ color: "#3A3A3A" }} />
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#0A0A0A" }}>Companies You Searched</span>
              <span style={{ fontSize: "11px", color: "#9AA0A6" }}>· {filteredSearchedCos.length} companies</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ minWidth: TABLE_MIN_W, borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
                <thead><TableHeader loadingStories={loadingStories} /></thead>
                <tbody>
                  {filteredSearchedCos.map(company => (
                    <SearchedRow key={company.permalink || company.domain} company={company} stories={stories} handleStatusChange={handleStatusChange} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && portfolioCompanies.length === 0 && filteredSearchedCos.length === 0 && (
          <div className="bg-white rounded-md p-12 text-center" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
            <Search size={32} style={{ color: "#DCDFE2", margin: "0 auto 10px" }} />
            <p style={{ fontSize: "13px", color: "#9AA0A6", fontStyle: "italic" }}>No company data available yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
